import { useCallback, useEffect, useState, type ReactNode } from "react";
import { api, ApiClientError } from "../lib/api";
import type {
  DailyResponse,
  HealthDailyResponse,
  ManualState,
  ManualWalkEntry,
  ManualWeightEntry,
  MeAuthenticated,
  SummaryResponse,
  WeeklyResponse,
} from "../lib/types";
import {
  fmtCalories,
  fmtDurationMinutes,
  fmtDurationSeconds,
  fmtFullDate,
  fmtMiles,
  fmtMinutes,
  fmtMonthDay,
  fmtNumber,
  fmtSignedDelta,
  fmtWeekLabel,
  todayIsoInTimezone,
} from "../lib/format";
import Header from "../components/Header";
import MetricCard from "../components/MetricCard";
import DailyChart from "../components/DailyChart";
import WeeklyChart from "../components/WeeklyChart";
import ProgressRing from "../components/ProgressRing";
import AzmDetail from "../components/AzmDetail";
import WeekDots from "../components/WeekDots";
import { DashboardSkeleton, ChartSkeleton } from "../components/LoadingSkeleton";
import ErrorState from "../components/ErrorState";
import EmptyState from "../components/EmptyState";
import Quest from "./Quest";

interface DashboardProps {
  me: MeAuthenticated;
  theme: { theme: "light" | "dark"; toggle: () => void };
}

interface DashboardData {
  summary: SummaryResponse;
  daily14: DailyResponse;
  daily28: DailyResponse;
  weekly: WeeklyResponse;
  healthDaily: HealthDailyResponse;
  manualState: ManualState;
}

interface DailyTargets {
  steps: number;
  zoneMinutes: number;
  distanceMiles: number;
  calories: number;
  sleepHours: number;
}

const DEFAULT_DAILY_TARGETS: DailyTargets = {
  steps: 10_000,
  zoneMinutes: 22,
  distanceMiles: 5,
  calories: 2200,
  sleepHours: 8,
};

const DAILY_TARGETS_STORAGE_KEY = "fitness-rpg:daily-targets:v1";
const DISMISSED_NOTICE_PREFIX = "fitness-rpg:dismissed-notice:";
const DASHBOARD_TAB_STORAGE_KEY = "fitness-rpg:dashboard-tab:v1";
type DashboardTab = "data" | "quest" | "manual";

export default function Dashboard({ me, theme }: DashboardProps) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      if (!me.connection.hasRefreshToken && me.connection.strava?.connected) {
        const [healthDaily, manual] = await Promise.all([
          api.stravaDaily(28),
          api.manualState(),
        ]);
        setData(buildDistanceOnlyDashboardData(healthDaily, manual.state));
        return;
      }
      const [summary, daily14, daily28, weekly, manual] = await Promise.all([
        api.summary(),
        api.daily(14),
        api.daily(28),
        api.weekly(12),
        api.manualState(),
      ]);
      let healthDaily: HealthDailyResponse;
      try {
        healthDaily = await api.healthDaily(28);
      } catch (e) {
        healthDaily = buildFallbackHealthDaily(daily28, e);
      }
      setData({
        summary,
        daily14,
        daily28,
        weekly,
        healthDaily,
        manualState: manual.state,
      });
    } catch (e) {
      setError(e);
    }
  }, [me.connection.hasRefreshToken, me.connection.strava?.connected]);

  useEffect(() => {
    load();
  }, [load]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (me.connection.hasRefreshToken) await api.refresh();
      await load();
    } catch (e) {
      // Reuse the dashboard error surface if refresh hit auth issues.
      if (e instanceof ApiClientError && e.code === "reauth_required") {
        setError(e);
      }
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  const connected =
    me.connection.hasRefreshToken || Boolean(me.connection.strava?.connected);
  const lastSyncAt = data?.summary.lastSyncAt ?? null;

  return (
    <div className="min-h-dvh">
      <Header
        me={me}
        theme={theme}
        lastSyncAt={lastSyncAt}
        loadingLastSync={refreshing}
        refreshing={refreshing}
        onRefresh={onRefresh}
        connected={connected}
      />
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        {!connected && (
          <div
            role="alert"
            className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-200"
          >
            We don't have a refresh token on file for this account.{" "}
            <a className="font-medium underline" href="/api/auth/start">
              Reconnect Google
            </a>{" "}
            to start syncing.
          </div>
        )}

        {error ? (
          <ErrorState error={error} onRetry={load} />
        ) : !data ? (
          <DashboardSkeleton />
        ) : (
          <Content data={data} />
        )}
      </main>
      <footer className="mx-auto max-w-6xl px-4 pb-8 text-[11px] text-zinc-500 sm:px-6">
        {data && (
          <span>
            Data via{" "}
            {data.healthDaily.source === "strava" ? "Strava" : "Google Health"} ·
            Times in{" "}
            <span className="font-medium">{data.summary.timezone}</span> ·{" "}
            <a className="underline hover:text-zinc-700 dark:hover:text-zinc-300" href="/privacy">
              Privacy
            </a>{" "}
            ·{" "}
            <a className="underline hover:text-zinc-700 dark:hover:text-zinc-300" href="/terms">
              Terms
            </a>
          </span>
        )}
      </footer>
    </div>
  );
}

function buildDistanceOnlyDashboardData(
  healthDaily: HealthDailyResponse,
  manualState: ManualState,
): DashboardData {
  const dailyRows = healthDaily.rows.map((row) => ({
    date: row.date,
    minutes: 0,
  }));
  return {
    summary: {
      today: 0,
      thisWeek: 0,
      last28: 0,
      recent28Average: 0,
      vsRecentAverage: 0,
      bestDay: null,
      bestWeek: null,
      currentStreak: streakFromDistance(healthDaily.rows),
      daysActiveThisWeek: healthDaily.rows.slice(-7).filter((row) => row.distanceMeters > 0).length,
      lastSyncAt: healthDaily.fetchedAt,
      timezone: healthDaily.timezone,
      rolling7Avg: 0,
    },
    daily14: {
      days: 14,
      timezone: healthDaily.timezone,
      startDate: healthDaily.rows.slice(-14)[0]?.date ?? healthDaily.startDate,
      endDate: healthDaily.endDate,
      fetchedAt: healthDaily.fetchedAt,
      fromCache: healthDaily.fromCache,
      rows: dailyRows.slice(-14),
    },
    daily28: {
      days: healthDaily.days,
      timezone: healthDaily.timezone,
      startDate: healthDaily.startDate,
      endDate: healthDaily.endDate,
      fetchedAt: healthDaily.fetchedAt,
      fromCache: healthDaily.fromCache,
      rows: dailyRows,
    },
    weekly: {
      weeks: 0,
      timezone: healthDaily.timezone,
      fetchedAt: healthDaily.fetchedAt,
      rows: [],
    },
    healthDaily,
    manualState,
  };
}

function buildFallbackHealthDaily(
  daily: DailyResponse,
  error: unknown,
): HealthDailyResponse {
  const totalAzm = sum(daily.rows.map((row) => row.minutes));
  const todayAzm = daily.rows.at(-1)?.minutes ?? 0;
  return {
    days: daily.days,
    timezone: daily.timezone,
    startDate: daily.startDate,
    endDate: daily.endDate,
    fetchedAt: daily.fetchedAt,
    fromCache: daily.fromCache,
    source: "google-health",
    warnings: [
      {
        dataType: "health-daily",
        message:
          "Detailed Google Health daily data failed. Active Zone Minutes loaded, but steps, distance, sleep, and heart-rate details are unavailable.",
        ...(error instanceof ApiClientError
          ? { status: error.upstream?.status ?? error.status }
          : {}),
        details: {
          error:
            error instanceof ApiClientError
              ? {
                  status: error.status,
                  code: error.code,
                  message: error.message,
                  upstream: error.upstream ?? null,
                }
              : {
                  message: error instanceof Error ? error.message : String(error),
                },
          fallbackActiveZoneMinutes: {
            today: todayAzm,
            total: totalAzm,
            nonZeroDays: daily.rows.filter((row) => row.minutes > 0).length,
            rows: daily.rows,
          },
          questMovement:
            "Paused unless manual miles are entered, because route progress uses actual steps or distance and the fallback only has Active Zone Minutes.",
        },
      },
    ],
    rows: daily.rows.map((row) => ({
      date: row.date,
      activeZoneMinutes: {
        total: row.minutes,
        fatBurn: row.minutes,
        cardio: 0,
        peak: 0,
      },
      steps: 0,
      distanceMeters: 0,
      calories: 0,
      heartRate: { average: null, min: null, max: null },
      timeInHeartRateZones: {
        lightSeconds: 0,
        moderateSeconds: 0,
        vigorousSeconds: 0,
        peakSeconds: 0,
      },
      heartRateZones: {
        light: null,
        moderate: null,
        vigorous: null,
        peak: null,
      },
      restingHeartRate: null,
      heartRateVariabilityMs: null,
      sleep: null,
    })),
  };
}

function streakFromDistance(rows: HealthDailyResponse["rows"]): number {
  let count = 0;
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i].distanceMeters <= 0) break;
    count++;
  }
  return count;
}

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

function hasHealthDailyFallback(healthDaily: HealthDailyResponse): boolean {
  const warnings = healthDaily.warnings ?? [];
  return (
    warnings.some((warning) => warning.dataType === "health-daily") ||
    (warnings.length > 0 &&
      healthDaily.source !== "strava" &&
      healthDaily.rows.every(
        (row) =>
          row.steps === 0 && row.distanceMeters === 0 && row.sleep === null,
      ))
  );
}

function hasAnyHealthData(healthDaily: HealthDailyResponse): boolean {
  return healthDaily.rows.some(
    (row) =>
      row.activeZoneMinutes.total > 0 ||
      row.steps > 0 ||
      row.distanceMeters > 0 ||
      row.calories > 0 ||
      row.sleep !== null ||
      row.heartRate.average !== null ||
      row.restingHeartRate !== null ||
      row.heartRateVariabilityMs !== null,
  );
}

function mergeManualWalksIntoHealthDaily(
  healthDaily: HealthDailyResponse,
  manualState: ManualState,
): HealthDailyResponse {
  if (manualState.walks.length === 0) return healthDaily;
  const rows = healthDaily.rows.map((row) => ({ ...row }));
  const byDate = new Map(rows.map((row) => [row.date, row]));
  for (const walk of manualState.walks) {
    const date = dateInTimezone(walk.occurredAt, healthDaily.timezone);
    const row = byDate.get(date);
    if (!row) continue;
    const meters = walk.miles * 1609.344;
    row.distanceMeters += meters;
    row.steps += Math.round(meters / 0.762);
  }
  return {
    ...healthDaily,
    rows,
  };
}

function dateInTimezone(iso: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(iso));
  const lookup: Record<string, string> = {};
  for (const part of parts) {
    if (part.type !== "literal") lookup[part.type] = part.value;
  }
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
}

function toDateTimeLocalValue(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function fromDateTimeLocalValue(value: string): string {
  return new Date(value).toISOString();
}

function Content({
  data,
}: {
  data: DashboardData;
}) {
  const [targets, setTargets] = useState(loadDailyTargets);
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<DashboardTab>(loadDashboardTab);
  const [manualState, setManualState] = useState(data.manualState);
  const { summary, daily14, daily28, weekly, healthDaily } = data;
  const questHealthDaily = mergeManualWalksIntoHealthDaily(
    healthDaily,
    manualState,
  );
  const todayHealth = healthDaily.rows[healthDaily.rows.length - 1];
  const today = todayIsoInTimezone(summary.timezone);
  const todayLabel = fmtFullDate(today);
  const distanceTargetMeters = targets.distanceMiles * 1609.344;
  const sleepTargetMinutes = targets.sleepHours * 60;
  const warningNotice =
    healthDaily.warnings && healthDaily.warnings.length > 0 ? (
      <HealthWarningNotice healthDaily={healthDaily} />
    ) : null;

  function setPersistedActiveTab(tab: DashboardTab) {
    setActiveTab(tab);
    saveDashboardTab(tab);
  }

  const noDataAtAll =
    healthDaily.source !== "strava" &&
    !healthDaily.warnings?.length &&
    !hasAnyHealthData(healthDaily) &&
    daily28.rows.every((r) => r.minutes === 0) &&
    weekly.rows.every((r) => r.minutes === 0);

  if (noDataAtAll) {
    return (
      <div className="space-y-6">
        <EmptyState
          title="No Active Zone Minutes yet"
          body="Once your Fitbit data is available in Google Health, your dashboard will populate here. Wear your tracker today and check back."
        />
      </div>
    );
  }

  if (activeTab === "quest") {
    return (
      <div className="space-y-6">
        <DashboardTabs activeTab={activeTab} onChange={setPersistedActiveTab} />
        {warningNotice}
        <Quest healthDaily={questHealthDaily} />
      </div>
    );
  }

  if (activeTab === "manual") {
    return (
      <div className="space-y-6">
        <DashboardTabs activeTab={activeTab} onChange={setPersistedActiveTab} />
        {warningNotice}
        <ManualTab state={manualState} onChange={setManualState} />
      </div>
    );
  }

  if (healthDaily.source === "strava") {
    return (
      <div className="space-y-6">
        <DashboardTabs activeTab={activeTab} onChange={setPersistedActiveTab} />
        {warningNotice}
        <StravaActivityDashboard healthDaily={healthDaily} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DashboardTabs activeTab={activeTab} onChange={setPersistedActiveTab} />
      {warningNotice}
      {hasHealthDailyFallback(healthDaily) && (
        <AzmFallbackOverview healthDaily={healthDaily} />
      )}
      <section aria-label="Today" className="card animate-slide-up p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-medium text-zinc-500">Today</p>
            <h1 className="text-2xl font-semibold tracking-tight">
              {todayLabel}
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Rings show progress toward default daily targets.
            </p>
          </div>
          <button
            type="button"
            className="btn-outline self-start"
            onClick={() => setTargetModalOpen(true)}
          >
            Targets
          </button>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-5">
          <ProgressRing
            value={todayHealth.steps}
            target={targets.steps}
            label="Steps"
            sublabel={`${fmtNumber(targets.steps)} target`}
          >
            <FootIcon />
          </ProgressRing>
          <ProgressRing
            value={todayHealth.activeZoneMinutes.total}
            target={targets.zoneMinutes}
            label="Zone Min"
            sublabel={`${fmtMinutes(targets.zoneMinutes)} target`}
          >
            <BoltIcon />
          </ProgressRing>
          <ProgressRing
            value={todayHealth.distanceMeters}
            target={distanceTargetMeters}
            label="Miles"
            displayValue={fmtMiles(todayHealth.distanceMeters)}
            sublabel={`${targets.distanceMiles.toLocaleString("en-US", {
              maximumFractionDigits: 1,
            })} mi target`}
          >
            <PinIcon />
          </ProgressRing>
          <ProgressRing
            value={todayHealth.calories}
            target={targets.calories}
            label="Cal"
            sublabel={`${fmtCalories(targets.calories)} target`}
          >
            <FlameIcon />
          </ProgressRing>
          <ProgressRing
            value={todayHealth.sleep?.minutesAsleep ?? 0}
            target={sleepTargetMinutes}
            label="Sleep"
            displayValue={fmtDurationMinutes(
              todayHealth.sleep?.minutesAsleep ?? 0,
            )}
            sublabel={`${targets.sleepHours.toLocaleString("en-US", {
              maximumFractionDigits: 1,
            })}h target`}
          >
            <MoonIcon />
          </ProgressRing>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <IntensityCard
            label="Peak AZM"
            value={fmtMinutes(todayHealth.activeZoneMinutes.peak)}
            colorClass="bg-violet-600"
            hint="Active Zone Minutes today"
          />
          <IntensityCard
            label="Vigorous"
            value={fmtDurationSeconds(
              todayHealth.timeInHeartRateZones.vigorousSeconds,
            )}
            colorClass="bg-blue-600"
            hint="heart-rate zone today"
          />
          <IntensityCard
            label="Peak HR"
            value={fmtDurationSeconds(
              todayHealth.timeInHeartRateZones.peakSeconds,
            )}
            colorClass="bg-fuchsia-600"
            hint="heart-rate zone today"
          />
        </div>
        {targetModalOpen && (
          <TargetModal
            targets={targets}
            onClose={() => setTargetModalOpen(false)}
            onSave={(next) => {
              setTargets(next);
              saveDailyTargets(next);
              setTargetModalOpen(false);
            }}
            onReset={() => {
              setTargets(DEFAULT_DAILY_TARGETS);
              saveDailyTargets(DEFAULT_DAILY_TARGETS);
              setTargetModalOpen(false);
            }}
          />
        )}
      </section>

      <section
        aria-label="Recovery and trends"
        className="grid animate-slide-up grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
      >
        <MetricCard
          label="Sleep duration"
          value={fmtDurationMinutes(todayHealth.sleep?.minutesAsleep ?? 0)}
          hint={
            todayHealth.sleep
              ? `${todayHealth.sleep.stages.deep}m deep · ${todayHealth.sleep.stages.rem}m REM`
              : "No sleep yet"
          }
        />
        <MetricCard
          label="Resting HR"
          value={
            todayHealth.restingHeartRate === null
              ? "—"
              : `${todayHealth.restingHeartRate}`
          }
          hint="beats per minute"
        />
        <MetricCard
          label="HRV"
          value={
            todayHealth.heartRateVariabilityMs === null
              ? "—"
              : `${Math.round(todayHealth.heartRateVariabilityMs)}`
          }
          hint="ms average"
        />
        <MetricCard
          label="This week"
          value={fmtMinutes(summary.thisWeek)}
          hint={
            <>
              {summary.daysActiveThisWeek} of 7 active days
              <WeekDots rows={daily14.rows} />
            </>
          }
        />
      </section>

      <section
        aria-label="Trends"
        className="grid animate-slide-up grid-cols-2 gap-3 sm:grid-cols-4"
      >
        <MetricCard
          label="Steps · 28d avg"
          value={fmtNumber(avg(healthDaily.rows.slice(-28).map((r) => r.steps)))}
          hint={`${fmtNumber(todayHealth.steps)} today`}
        />
        <MetricCard
          label="Last 28 days"
          value={fmtMinutes(summary.last28)}
          hint={`Avg ${(summary.last28 / 28).toFixed(1)} AZM/day`}
        />
        <MetricCard
          label="Vs recent avg"
          value={
            <span
              className={
                summary.vsRecentAverage >= 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-zinc-700 dark:text-zinc-300"
              }
            >
              {fmtSignedDelta(summary.vsRecentAverage)}
            </span>
          }
          hint={`Today vs prior 27-day average (${summary.recent28Average.toFixed(
            1,
          )})`}
        />
        <MetricCard
          label="Best AZM day · 28d"
          value={summary.bestDay ? fmtMinutes(summary.bestDay.minutes) : "—"}
          hint={summary.bestDay ? fmtFullDate(summary.bestDay.date) : "Nothing yet"}
        />
      </section>

      <AzmDetail rows={healthDaily.rows} />

      {/* Daily charts */}
      <section
        aria-label="Daily charts"
        className="grid animate-slide-up grid-cols-1 gap-4 lg:grid-cols-2"
      >
        <div className="card p-5">
          <ChartHeader
            title="Last 14 days"
            subtitle="Daily Active Zone Minutes"
          />
          {daily14.rows.length ? (
            <DailyChart rows={daily14.rows} height={220} />
          ) : (
            <ChartSkeleton height={220} />
          )}
        </div>
        <div className="card p-5">
          <ChartHeader
            title="Last 28 days"
            subtitle="Daily AZM with 7-day average"
          />
          {daily28.rows.length ? (
            <DailyChart
              rows={daily28.rows}
              showRollingAverage
              showWeekdayLabels={false}
              height={220}
            />
          ) : (
            <ChartSkeleton height={220} />
          )}
        </div>
      </section>

      {/* Weekly chart */}
      <section aria-label="Weekly totals" className="animate-slide-up">
        <div className="card p-5">
          <ChartHeader
            title="Weekly totals · 12 weeks"
            subtitle="Monday-start weeks · current week highlighted"
          />
          {weekly.rows.length ? (
            <WeeklyChart rows={weekly.rows} height={240} />
          ) : (
            <ChartSkeleton height={240} />
          )}
        </div>
      </section>

      {/* Records */}
      <section
        aria-label="Records"
        className="grid animate-slide-up grid-cols-1 gap-3 sm:grid-cols-2"
      >
        <MetricCard
          label="Best week · 12 weeks"
          value={
            summary.bestWeek ? fmtMinutes(summary.bestWeek.minutes) : "—"
          }
          hint={
            summary.bestWeek
              ? fmtWeekLabel(summary.bestWeek.weekStart)
              : "Nothing yet"
          }
        />
        <MetricCard
          label="Current streak"
          value={
            summary.currentStreak === 0
              ? "—"
              : `${summary.currentStreak} day${
                  summary.currentStreak === 1 ? "" : "s"
                }`
          }
          hint="Consecutive days with any AZM"
        />
      </section>
    </div>
  );
}

function StravaActivityDashboard({
  healthDaily,
}: {
  healthDaily: HealthDailyResponse;
}) {
  const [sourceNoticeVisible, setSourceNoticeVisible] = useState(() =>
    loadDismissibleNotice("strava-source-notice"),
  );
  const rows = healthDaily.rows;
  const today = rows[rows.length - 1];
  const last7 = rows.slice(-7);
  const totalMeters = sum(rows.map((row) => row.distanceMeters));
  const totalActivities = sum(
    rows.map((row) => row.strava?.activityCount ?? 0),
  );
  const totalMovingSeconds = sum(
    rows.map((row) => row.strava?.movingSeconds ?? 0),
  );
  const weekMeters = sum(last7.map((row) => row.distanceMeters));
  const weekActivities = sum(last7.map((row) => row.strava?.activityCount ?? 0));
  const bestDay =
    rows.reduce<(typeof rows)[number] | null>(
      (best, row) =>
        !best || row.distanceMeters > best.distanceMeters ? row : best,
      null,
    ) ?? null;
  const activeDays = rows.filter((row) => row.distanceMeters > 0).length;
  const streak = streakFromDistance(rows);
  const chartMax = Math.max(1, ...rows.map((row) => row.distanceMeters));

  return (
    <div className="space-y-6">
      {sourceNoticeVisible && (
        <DismissibleNotice
          storageKey="strava-source-notice"
          title="Strava activity source"
          onDismiss={() => setSourceNoticeVisible(false)}
        >
          Strava supplies activity distance and moving time for this view and
          for quest movement. Connect Google Health if you also want Active
          Zone Minutes, sleep, heart-rate, and recovery details.
        </DismissibleNotice>
      )}
      <section className="card animate-slide-up p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-medium text-accent">Strava activity</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight">
              Quest movement record
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-600 dark:text-zinc-400">
              Runs, walks, and hikes from Strava appear here as activity
              records and move quests by distance.
            </p>
          </div>
          <a href="/api/auth/start" className="btn-outline self-start">
            Connect Google Health
          </a>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Today"
            value={`${fmtMiles(today?.distanceMeters ?? 0)} mi`}
            hint={`${today?.strava?.activityCount ?? 0} activities`}
            emphasis="accent"
          />
          <MetricCard
            label="This week"
            value={`${fmtMiles(weekMeters)} mi`}
            hint={`${weekActivities} activities`}
          />
          <MetricCard
            label="Last 28 days"
            value={`${fmtMiles(totalMeters)} mi`}
            hint={`${totalActivities} activities`}
          />
          <MetricCard
            label="Moving time"
            value={fmtDurationSeconds(totalMovingSeconds)}
            hint="Last 28 days"
          />
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <MetricCard
          label="Best day · 28d"
          value={bestDay ? `${fmtMiles(bestDay.distanceMeters)} mi` : "—"}
          hint={bestDay ? fmtFullDate(bestDay.date) : "No activities yet"}
        />
        <MetricCard
          label="Active days"
          value={`${activeDays}/${rows.length}`}
          hint="Days with Strava distance"
        />
        <MetricCard
          label="Current streak"
          value={`${streak} days`}
          hint="Consecutive days with activity distance"
        />
      </section>

      <section className="card p-5">
        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold">Daily distance</h2>
            <p className="text-sm text-zinc-500">
              Runs, walks, hikes, and trail runs counted from Strava.
            </p>
          </div>
          <p className="text-xs text-zinc-500">
            {fmtMonthDay(healthDaily.startDate)} -{" "}
            {fmtMonthDay(healthDaily.endDate)}
          </p>
        </div>
        <div className="flex h-56 items-end gap-1 rounded-xl border border-zinc-200 p-3 dark:border-zinc-800">
          {rows.map((row) => {
            const height = Math.max(2, (row.distanceMeters / chartMax) * 100);
            return (
              <div
                key={row.date}
                className="flex min-w-0 flex-1 flex-col items-center gap-2"
              >
                <div className="flex h-40 w-full items-end justify-center">
                  <div
                    className="w-full max-w-5 rounded-t bg-accent"
                    style={{ height: `${height}%` }}
                    title={`${fmtFullDate(row.date)}: ${fmtMiles(
                      row.distanceMeters,
                    )} miles`}
                  />
                </div>
                <span className="hidden text-[10px] text-zinc-500 sm:block">
                  {row.date.endsWith("-01") || row === rows[rows.length - 1]
                    ? fmtMonthDay(row.date)
                    : ""}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="card p-5">
        <h2 className="text-lg font-semibold">Recent activity days</h2>
        <div className="mt-4 divide-y divide-zinc-200 dark:divide-zinc-800">
          {rows
            .filter((row) => row.distanceMeters > 0)
            .slice(-7)
            .reverse()
            .map((row) => (
              <div
                key={row.date}
                className="grid grid-cols-[1fr_auto] gap-3 py-3 text-sm"
              >
                <div>
                  <p className="font-medium">{fmtFullDate(row.date)}</p>
                  <p className="mt-1 text-zinc-500">
                    {row.strava?.activityCount ?? 0} activities ·{" "}
                    {fmtDurationSeconds(row.strava?.movingSeconds ?? 0)}
                  </p>
                </div>
                <p className="font-semibold tabular-nums">
                  {fmtMiles(row.distanceMeters)} mi
                </p>
              </div>
            ))}
          {rows.every((row) => row.distanceMeters <= 0) && (
            <p className="py-3 text-sm text-zinc-500">
              No Strava runs, walks, or hikes found in this range yet.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

function ManualTab({
  state,
  onChange,
}: {
  state: ManualState;
  onChange: (state: ManualState) => void;
}) {
  const [walkMiles, setWalkMiles] = useState("");
  const [walkAt, setWalkAt] = useState(toDateTimeLocalValue(new Date().toISOString()));
  const [walkNote, setWalkNote] = useState("");
  const [weightPounds, setWeightPounds] = useState("");
  const [weightAt, setWeightAt] = useState(toDateTimeLocalValue(new Date().toISOString()));
  const [weightNote, setWeightNote] = useState("");
  const [saving, setSaving] = useState(false);

  async function save(next: ManualState) {
    setSaving(true);
    try {
      const saved = await api.saveManualState(next);
      onChange(saved.state);
    } finally {
      setSaving(false);
    }
  }

  async function addWalk() {
    const miles = Number(walkMiles);
    if (!Number.isFinite(miles) || miles <= 0) return;
    const next: ManualState = {
      ...state,
      walks: [
        ...state.walks,
        {
          id: crypto.randomUUID(),
          occurredAt: fromDateTimeLocalValue(walkAt),
          miles,
          note: walkNote.trim() || undefined,
        },
      ].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt)),
      updatedAt: new Date().toISOString(),
    };
    await save(next);
    setWalkMiles("");
    setWalkNote("");
  }

  async function addWeight() {
    const pounds = Number(weightPounds);
    if (!Number.isFinite(pounds) || pounds <= 0) return;
    const next: ManualState = {
      ...state,
      weights: [
        ...state.weights,
        {
          id: crypto.randomUUID(),
          recordedAt: fromDateTimeLocalValue(weightAt),
          pounds,
          note: weightNote.trim() || undefined,
        },
      ].sort((a, b) => a.recordedAt.localeCompare(b.recordedAt)),
      updatedAt: new Date().toISOString(),
    };
    await save(next);
    setWeightPounds("");
    setWeightNote("");
  }

  async function deleteWalk(id: string) {
    await save({
      ...state,
      walks: state.walks.filter((walk) => walk.id !== id),
      updatedAt: new Date().toISOString(),
    });
  }

  async function deleteWeight(id: string) {
    await save({
      ...state,
      weights: state.weights.filter((weight) => weight.id !== id),
      updatedAt: new Date().toISOString(),
    });
  }

  async function updateWalk(entry: ManualWalkEntry, patch: Partial<ManualWalkEntry>) {
    await save({
      ...state,
      walks: state.walks.map((walk) =>
        walk.id === entry.id ? { ...walk, ...patch } : walk,
      ),
      updatedAt: new Date().toISOString(),
    });
  }

  async function updateWeight(
    entry: ManualWeightEntry,
    patch: Partial<ManualWeightEntry>,
  ) {
    await save({
      ...state,
      weights: state.weights.map((weight) =>
        weight.id === entry.id ? { ...weight, ...patch } : weight,
      ),
      updatedAt: new Date().toISOString(),
    });
  }

  async function setWeightGraphVisible(weightGraphVisible: boolean) {
    await save({
      ...state,
      preferences: {
        ...state.preferences,
        weightGraphVisible,
      },
      updatedAt: new Date().toISOString(),
    });
  }

  const totalManualMiles = sum(state.walks.map((walk) => walk.miles));
  const latestWeight = state.weights.at(-1);

  return (
    <div className="space-y-6">
      <section className="card p-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-medium text-accent">Manual</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight">
              Manual records
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-600 dark:text-zinc-400">
              Add walking miles when your source app missed them. Weight
              records are optional and stay hidden unless you show the graph.
            </p>
          </div>
          {saving && <span className="chip bg-accent-soft text-accent">Saving</span>}
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <MetricCard
            label="Manual miles"
            value={fmtMiles(totalManualMiles * 1609.344)}
            hint={`${state.walks.length} entries`}
          />
          <MetricCard
            label="Latest weight"
            value={latestWeight ? `${latestWeight.pounds.toFixed(1)} lb` : "—"}
            hint={latestWeight ? fmtFullDate(latestWeight.recordedAt.slice(0, 10)) : "Optional"}
          />
          <MetricCard
            label="Weight records"
            value={fmtNumber(state.weights.length)}
            hint={state.preferences.weightGraphVisible ? "Graph shown" : "Graph hidden"}
          />
        </div>
      </section>

      <section className="card p-5">
        <h2 className="text-lg font-semibold">Add walk</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
          <label className="text-sm">
            <span className="text-zinc-500">Miles walked</span>
            <input
              className="mt-1 w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-950"
              inputMode="decimal"
              value={walkMiles}
              onChange={(event) => setWalkMiles(event.target.value)}
              placeholder="2.5"
            />
          </label>
          <label className="text-sm">
            <span className="text-zinc-500">When</span>
            <input
              className="mt-1 w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-950"
              type="datetime-local"
              value={walkAt}
              onChange={(event) => setWalkAt(event.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="text-zinc-500">Note</span>
            <input
              className="mt-1 w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-950"
              value={walkNote}
              onChange={(event) => setWalkNote(event.target.value)}
              placeholder="Optional"
            />
          </label>
          <button
            type="button"
            className="btn-primary"
            disabled={saving}
            onClick={() => void addWalk()}
          >
            Add walk
          </button>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="text-lg font-semibold">Walk log</h2>
        <div className="mt-4 divide-y divide-zinc-200 dark:divide-zinc-800">
          {state.walks
            .slice()
            .reverse()
            .map((walk) => (
              <ManualWalkRow
                key={walk.id}
                walk={walk}
                onDelete={() => void deleteWalk(walk.id)}
                onUpdate={(patch) => void updateWalk(walk, patch)}
              />
            ))}
          {state.walks.length === 0 && (
            <p className="py-3 text-sm text-zinc-500">
              No manual walks yet.
            </p>
          )}
        </div>
      </section>

      <section className="card p-5">
        <h2 className="text-lg font-semibold">Add weight</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
          <label className="text-sm">
            <span className="text-zinc-500">Weight</span>
            <input
              className="mt-1 w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-950"
              inputMode="decimal"
              value={weightPounds}
              onChange={(event) => setWeightPounds(event.target.value)}
              placeholder="175.0"
            />
          </label>
          <label className="text-sm">
            <span className="text-zinc-500">When</span>
            <input
              className="mt-1 w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-950"
              type="datetime-local"
              value={weightAt}
              onChange={(event) => setWeightAt(event.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="text-zinc-500">Note</span>
            <input
              className="mt-1 w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-950"
              value={weightNote}
              onChange={(event) => setWeightNote(event.target.value)}
              placeholder="Optional"
            />
          </label>
          <button
            type="button"
            className="btn-primary"
            disabled={saving}
            onClick={() => void addWeight()}
          >
            Add weight
          </button>
        </div>
      </section>

      <section className="card p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold">Weight log</h2>
            <p className="mt-1 text-sm text-zinc-500">
              The graph stays hidden until you explicitly show it.
            </p>
          </div>
          <button
            type="button"
            className="btn-outline"
            onClick={() =>
              void setWeightGraphVisible(!state.preferences.weightGraphVisible)
            }
          >
            {state.preferences.weightGraphVisible ? "Hide graph" : "Show graph"}
          </button>
        </div>
        {state.preferences.weightGraphVisible && (
          <WeightGraph weights={state.weights} />
        )}
        <div className="mt-4 divide-y divide-zinc-200 dark:divide-zinc-800">
          {state.weights
            .slice()
            .reverse()
            .map((weight) => (
              <ManualWeightRow
                key={weight.id}
                weight={weight}
                onDelete={() => void deleteWeight(weight.id)}
                onUpdate={(patch) => void updateWeight(weight, patch)}
              />
            ))}
          {state.weights.length === 0 && (
            <p className="py-3 text-sm text-zinc-500">
              No weight records yet.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

function ManualWalkRow({
  walk,
  onDelete,
  onUpdate,
}: {
  walk: ManualWalkEntry;
  onDelete: () => void;
  onUpdate: (patch: Partial<ManualWalkEntry>) => void;
}) {
  return (
    <div className="grid gap-3 py-3 text-sm md:grid-cols-[1fr_120px_1fr_auto] md:items-center">
      <input
        type="datetime-local"
        className="rounded-lg border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-950"
        value={toDateTimeLocalValue(walk.occurredAt)}
        onChange={(event) =>
          onUpdate({ occurredAt: fromDateTimeLocalValue(event.target.value) })
        }
        aria-label="Walk time"
      />
      <input
        className="rounded-lg border border-zinc-200 bg-white px-3 py-2 tabular-nums dark:border-zinc-800 dark:bg-zinc-950"
        inputMode="decimal"
        value={String(walk.miles)}
        onChange={(event) => {
          const miles = Number(event.target.value);
          if (Number.isFinite(miles) && miles > 0) onUpdate({ miles });
        }}
        aria-label="Walk miles"
      />
      <input
        className="rounded-lg border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-950"
        value={walk.note ?? ""}
        onChange={(event) => onUpdate({ note: event.target.value || undefined })}
        placeholder="Optional note"
        aria-label="Walk note"
      />
      <button type="button" className="btn-ghost" onClick={onDelete}>
        Remove
      </button>
    </div>
  );
}

function ManualWeightRow({
  weight,
  onDelete,
  onUpdate,
}: {
  weight: ManualWeightEntry;
  onDelete: () => void;
  onUpdate: (patch: Partial<ManualWeightEntry>) => void;
}) {
  return (
    <div className="grid gap-3 py-3 text-sm md:grid-cols-[1fr_120px_1fr_auto] md:items-center">
      <input
        type="datetime-local"
        className="rounded-lg border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-950"
        value={toDateTimeLocalValue(weight.recordedAt)}
        onChange={(event) =>
          onUpdate({ recordedAt: fromDateTimeLocalValue(event.target.value) })
        }
        aria-label="Weight time"
      />
      <input
        className="rounded-lg border border-zinc-200 bg-white px-3 py-2 tabular-nums dark:border-zinc-800 dark:bg-zinc-950"
        inputMode="decimal"
        value={String(weight.pounds)}
        onChange={(event) => {
          const pounds = Number(event.target.value);
          if (Number.isFinite(pounds) && pounds > 0) onUpdate({ pounds });
        }}
        aria-label="Weight pounds"
      />
      <input
        className="rounded-lg border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-950"
        value={weight.note ?? ""}
        onChange={(event) => onUpdate({ note: event.target.value || undefined })}
        placeholder="Optional note"
        aria-label="Weight note"
      />
      <button type="button" className="btn-ghost" onClick={onDelete}>
        Remove
      </button>
    </div>
  );
}

function WeightGraph({ weights }: { weights: ManualWeightEntry[] }) {
  const sorted = weights.slice().sort((a, b) => a.recordedAt.localeCompare(b.recordedAt));
  if (sorted.length === 0) {
    return (
      <div className="mt-4 rounded-xl border border-dashed border-zinc-200 p-5 text-sm text-zinc-500 dark:border-zinc-800">
        Add at least one weight record to start the graph.
      </div>
    );
  }
  const min = Math.min(...sorted.map((entry) => entry.pounds));
  const max = Math.max(...sorted.map((entry) => entry.pounds));
  const range = Math.max(1, max - min);
  const points = sorted
    .map((entry, index) => {
      const x = sorted.length === 1 ? 50 : (index / (sorted.length - 1)) * 100;
      const y = 90 - ((entry.pounds - min) / range) * 70;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <div className="mt-4 rounded-xl border border-zinc-200 p-4 dark:border-zinc-800">
      <svg viewBox="0 0 100 100" className="h-48 w-full" role="img" aria-label="Weight graph">
        <polyline
          points={points}
          fill="none"
          stroke="var(--accent)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {sorted.map((entry, index) => {
          const x = sorted.length === 1 ? 50 : (index / (sorted.length - 1)) * 100;
          const y = 90 - ((entry.pounds - min) / range) * 70;
          return <circle key={entry.id} cx={x} cy={y} r="2.5" fill="var(--accent)" />;
        })}
      </svg>
      <div className="mt-2 flex justify-between text-xs text-zinc-500">
        <span>{fmtFullDate(sorted[0].recordedAt.slice(0, 10))}</span>
        <span>
          {min.toFixed(1)}-{max.toFixed(1)} lb
        </span>
        <span>{fmtFullDate(sorted.at(-1)!.recordedAt.slice(0, 10))}</span>
      </div>
    </div>
  );
}

function DashboardTabs({
  activeTab,
  onChange,
}: {
  activeTab: DashboardTab;
  onChange: (tab: DashboardTab) => void;
}) {
  return (
    <div className="inline-flex rounded-xl border border-zinc-200 bg-white p-1 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
      <button
        type="button"
        onClick={() => onChange("data")}
        className={tabClass(activeTab === "data")}
      >
        Data
      </button>
      <button
        type="button"
        onClick={() => onChange("quest")}
        className={tabClass(activeTab === "quest")}
      >
        Quest
      </button>
      <button
        type="button"
        onClick={() => onChange("manual")}
        className={tabClass(activeTab === "manual")}
      >
        Manual
      </button>
    </div>
  );
}

function DismissibleNotice({
  storageKey,
  title,
  children,
  onDismiss,
}: {
  storageKey: string;
  title: string;
  children: ReactNode;
  onDismiss: () => void;
}) {
  function dismiss() {
    saveDismissibleNotice(storageKey);
    onDismiss();
  }

  return (
    <section className="rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-cyan-950 dark:border-cyan-900 dark:bg-cyan-950/50 dark:text-cyan-100">
      <div className="flex gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">{title}</h2>
          <p className="mt-1 leading-6">{children}</p>
        </div>
        <button
          type="button"
          className="self-start rounded-md px-2 py-1 text-xs font-medium text-cyan-800 hover:bg-cyan-100 dark:text-cyan-100 dark:hover:bg-cyan-900"
          onClick={dismiss}
        >
          Dismiss
        </button>
      </div>
    </section>
  );
}

function AzmFallbackOverview({
  healthDaily,
}: {
  healthDaily: HealthDailyResponse;
}) {
  const rows = healthDaily.rows.map((row) => ({
    date: row.date,
    minutes: row.activeZoneMinutes.total,
  }));
  const total = sum(rows.map((row) => row.minutes));
  const activeDays = rows.filter((row) => row.minutes > 0).length;
  const hasAzm = total > 0;
  const today = rows.at(-1) ?? null;
  const latestActive = rows
    .slice()
    .reverse()
    .find((row) => row.minutes > 0);

  return (
    <section
      aria-label="Active Zone Minutes fallback summary"
      className="card animate-slide-up p-5"
    >
      <div className="mb-4">
        <p className="text-sm font-medium text-amber-600 dark:text-amber-300">
          AZM fallback mode
        </p>
        <h2 className="text-xl font-semibold tracking-tight">
          {hasAzm ? "Active Zone Minutes loaded" : "Google Health details unavailable"}
        </h2>
        <p className="mt-1 max-w-3xl text-sm leading-6 text-zinc-600 dark:text-zinc-300">
          {hasAzm
            ? "Google Health returned Active Zone Minutes, but the richer daily request for steps, distance, sleep, and heart-rate details failed."
            : "Google Health did not return readable daily activity details for this range. This can happen before Fitbit data is available, when Google Health is still syncing, or when Google rejects the detailed daily request."}{" "}
          Quest progress stays paused unless Strava distance or manual miles are
          available.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Last 28 days"
          value={fmtMinutes(total)}
          hint={`${activeDays} of ${rows.length} active days`}
        />
        <MetricCard
          label="Latest active AZM day"
          value={latestActive ? fmtMinutes(latestActive.minutes) : "—"}
          hint={latestActive ? fmtFullDate(latestActive.date) : "No active days"}
        />
        <MetricCard
          label="Current local day"
          value={today ? fmtMinutes(today.minutes) : "—"}
          hint={today ? fmtFullDate(today.date) : "No row returned"}
        />
        <MetricCard
          label="Movement data"
          value="Unavailable"
          hint="Steps, miles, sleep, and heart-rate details did not load"
        />
      </div>
    </section>
  );
}

function HealthWarningNotice({
  healthDaily,
}: {
  healthDaily: HealthDailyResponse;
}) {
  const [copied, setCopied] = useState(false);
  const warnings = healthDaily.warnings ?? [];
  const rowSummary = summarizeHealthRows(healthDaily);
  const hasAzm = rowSummary.activeZoneMinutes.total > 0;
  const payload = {
    source: healthDaily.source ?? "google-health",
    range: {
      days: healthDaily.days,
      startDate: healthDaily.startDate,
      endDate: healthDaily.endDate,
      timezone: healthDaily.timezone,
    },
    fetchedAt: healthDaily.fetchedAt,
    rowSummary,
    warnings,
  };

  async function copyDiagnostics() {
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <section
      className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-100"
      aria-label="Data warning"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-semibold">Some Google Health details did not load</h2>
          <p className="mt-1 leading-6">
            {hasAzm
              ? "Active Zone Minutes are still available, but steps, distance, sleep, or heart-rate details may be missing."
              : "Google Health did not return readable daily activity details for this range."}{" "}
            Quest movement needs steps, distance, or manual miles, so it may
            pause until those details load again. Share the details below if
            this keeps happening.
          </p>
        </div>
        <button
          type="button"
          className="btn-outline self-start bg-white/70 dark:bg-zinc-950/40"
          onClick={() => void copyDiagnostics()}
        >
          {copied ? "Copied" : "Copy details"}
        </button>
      </div>
      <details className="mt-3">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider">
          Details
        </summary>
        <pre className="mt-2 max-h-56 overflow-auto rounded-lg bg-white/70 p-3 text-xs leading-5 text-amber-950 dark:bg-zinc-950/50 dark:text-amber-100">
          {JSON.stringify(payload, null, 2)}
        </pre>
      </details>
    </section>
  );
}

function summarizeHealthRows(healthDaily: HealthDailyResponse) {
  const rows = healthDaily.rows;
  const today = rows.at(-1);
  return {
    rowCount: rows.length,
    activeZoneMinutes: {
      today: today?.activeZoneMinutes.total ?? 0,
      total: sum(rows.map((row) => row.activeZoneMinutes.total)),
      nonZeroDays: rows.filter((row) => row.activeZoneMinutes.total > 0).length,
    },
    steps: {
      today: today?.steps ?? 0,
      total: sum(rows.map((row) => row.steps)),
      nonZeroDays: rows.filter((row) => row.steps > 0).length,
    },
    distanceMeters: {
      today: today?.distanceMeters ?? 0,
      total: sum(rows.map((row) => row.distanceMeters)),
      nonZeroDays: rows.filter((row) => row.distanceMeters > 0).length,
    },
    sleepDays: rows.filter((row) => row.sleep !== null).length,
  };
}

function tabClass(active: boolean): string {
  return [
    "rounded-lg px-4 py-2 text-sm font-medium transition-colors",
    active
      ? "bg-accent text-white"
      : "text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800",
  ].join(" ");
}

function ChartHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="mb-3">
      <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
      {subtitle && (
        <p className="text-xs text-zinc-500">{subtitle}</p>
      )}
    </div>
  );
}

function avg(values: number[]): number {
  const finite = values.filter(Number.isFinite);
  if (!finite.length) return 0;
  return finite.reduce((sum, value) => sum + value, 0) / finite.length;
}

function loadDailyTargets(): DailyTargets {
  if (typeof window === "undefined") return DEFAULT_DAILY_TARGETS;
  try {
    const raw = window.localStorage.getItem(DAILY_TARGETS_STORAGE_KEY);
    if (!raw) return DEFAULT_DAILY_TARGETS;
    return normalizeDailyTargets(JSON.parse(raw));
  } catch {
    return DEFAULT_DAILY_TARGETS;
  }
}

function saveDailyTargets(targets: DailyTargets): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    DAILY_TARGETS_STORAGE_KEY,
    JSON.stringify(targets),
  );
}

function loadDismissibleNotice(key: string): boolean {
  if (typeof window === "undefined") return true;
  return window.localStorage.getItem(`${DISMISSED_NOTICE_PREFIX}${key}`) !== "1";
}

function saveDismissibleNotice(key: string): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(`${DISMISSED_NOTICE_PREFIX}${key}`, "1");
}

function loadDashboardTab(): DashboardTab {
  if (typeof window === "undefined") return "data";
  const value = window.localStorage.getItem(DASHBOARD_TAB_STORAGE_KEY);
  return value === "quest" || value === "manual" || value === "data"
    ? value
    : "data";
}

function saveDashboardTab(tab: DashboardTab): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(DASHBOARD_TAB_STORAGE_KEY, tab);
}

function normalizeDailyTargets(value: unknown): DailyTargets {
  const record =
    value && typeof value === "object"
      ? (value as Partial<Record<keyof DailyTargets, unknown>>)
      : {};
  return {
    steps: boundedNumber(record.steps, DEFAULT_DAILY_TARGETS.steps, 1000, 50000),
    zoneMinutes: boundedNumber(
      record.zoneMinutes,
      DEFAULT_DAILY_TARGETS.zoneMinutes,
      1,
      300,
    ),
    distanceMiles: boundedNumber(
      record.distanceMiles,
      DEFAULT_DAILY_TARGETS.distanceMiles,
      0.1,
      100,
    ),
    calories: boundedNumber(
      record.calories,
      DEFAULT_DAILY_TARGETS.calories,
      100,
      10000,
    ),
    sleepHours: boundedNumber(
      record.sleepHours,
      DEFAULT_DAILY_TARGETS.sleepHours,
      1,
      16,
    ),
  };
}

function boundedNumber(
  value: unknown,
  fallback: number,
  min: number,
  max: number,
): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function TargetModal({
  targets,
  onSave,
  onReset,
  onClose,
}: {
  targets: DailyTargets;
  onSave: (targets: DailyTargets) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(targets);

  const setField = (field: keyof DailyTargets, value: string) => {
    setDraft((current) => ({ ...current, [field]: Number(value) }));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end bg-black/40 p-4 sm:items-center sm:justify-center"
      role="presentation"
      onMouseDown={onClose}
    >
      <form
        className="card w-full max-w-lg p-5"
        role="dialog"
        aria-modal="true"
        aria-labelledby="target-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          onSave(normalizeDailyTargets(draft));
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="target-modal-title" className="text-lg font-semibold">
              Daily targets
            </h2>
            <p className="mt-1 text-sm text-zinc-500">
              These are stored in this browser and control the Today rings.
            </p>
          </div>
          <button type="button" className="btn-ghost px-2" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TargetField
            label="Steps"
            value={draft.steps}
            min={1000}
            max={50000}
            step={500}
            onChange={(value) => setField("steps", value)}
          />
          <TargetField
            label="Zone minutes"
            value={draft.zoneMinutes}
            min={1}
            max={300}
            step={1}
            onChange={(value) => setField("zoneMinutes", value)}
          />
          <TargetField
            label="Miles"
            value={draft.distanceMiles}
            min={0.1}
            max={100}
            step={0.1}
            onChange={(value) => setField("distanceMiles", value)}
          />
          <TargetField
            label="Calories"
            value={draft.calories}
            min={100}
            max={10000}
            step={50}
            onChange={(value) => setField("calories", value)}
          />
          <TargetField
            label="Sleep hours"
            value={draft.sleepHours}
            min={1}
            max={16}
            step={0.25}
            onChange={(value) => setField("sleepHours", value)}
          />
        </div>

        <div className="mt-5 flex flex-wrap justify-between gap-3">
          <button type="button" className="btn-outline" onClick={onReset}>
            Reset defaults
          </button>
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Save
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function TargetField({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
        {label}
      </span>
      <input
        type="number"
        className="mt-1 w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-950 outline-none transition-colors focus:border-accent dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-50"
        value={Number.isFinite(value) ? value : ""}
        min={min}
        max={max}
        step={step}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function IntensityCard({
  label,
  value,
  colorClass,
  hint,
}: {
  label: string;
  value: string;
  colorClass: string;
  hint: string;
}) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-950/50">
      <div className="flex items-center gap-2">
        <span className={`h-2.5 w-2.5 rounded-full ${colorClass}`} />
        <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
          {label}
        </p>
      </div>
      <p className="tnum mt-2 text-3xl font-semibold leading-none tracking-tight">
        {value}
      </p>
      <p className="mt-1 text-xs text-zinc-500">{hint}</p>
    </div>
  );
}

function RingIcon({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-7 w-7"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function FootIcon() {
  return (
    <RingIcon>
      <path d="M8 20c1.5 1.2 4.7 1.2 6.2-.4 1.7-1.8.7-4.5-.8-6.4-1.1-1.4-1.2-3.6-.4-5.2.7-1.3.4-3.1-1-3.8-1.7-.8-3.7.2-4.3 2.2-.9 2.9-.9 6-.2 8.9.2.9-.3 1.8-.9 2.5-.6.8-.3 1.7 1.4 2.2Z" />
      <path d="M16.5 10.5c1.2.6 2.4 1.7 2.8 3.1.5 1.7-.3 3.2-1.8 3.7" />
    </RingIcon>
  );
}

function BoltIcon() {
  return (
    <RingIcon>
      <path d="m13 2-7 12h6l-1 8 7-12h-6l1-8Z" />
    </RingIcon>
  );
}

function PinIcon() {
  return (
    <RingIcon>
      <path d="M12 21s7-5.6 7-12a7 7 0 1 0-14 0c0 6.4 7 12 7 12Z" />
      <circle cx="12" cy="9" r="2" />
    </RingIcon>
  );
}

function FlameIcon() {
  return (
    <RingIcon>
      <path d="M12 22c4 0 7-2.8 7-6.7 0-2.6-1.5-4.8-3.5-6.8-.7 1.8-2 2.8-3.5 3.3.4-2.9-.5-5.4-3-7.8-.4 3.5-2.9 5.8-4 8.2C3.5 14 5 22 12 22Z" />
    </RingIcon>
  );
}

function MoonIcon() {
  return (
    <RingIcon>
      <path d="M20 14.8A8.2 8.2 0 0 1 9.2 4 8.5 8.5 0 1 0 20 14.8Z" />
      <path d="M17 4v4" />
      <path d="M15 6h4" />
    </RingIcon>
  );
}
